// src/data/pbWorkspace.js — per-tenant workspace hydration from PocketBase.
//
// Fix #1 (docs/deploy_fixes.md): after OTP login the app must show the logged-in
// tenant's OWN business data, not the mock "Northstar" demo. This module reads
// the tenant's shared collections through the PB SDK (rules enforce tenant
// scoping server-side — the client never filters) and maps them into the exact
// camelCase state shape dataClient/screens already consume, so screens change
// little or nothing.
//
// Confidential collections (chats, memory, credentials, plans…) stay in Dexie —
// never routed through PB (owner-confirmed invariant). Only the 6 system
// collections below are hydrated here.

import { pb } from '../services/pb.js';

// Client model-tier ids used across the UI ('tier_deep' etc.) map to the PB
// roster-alias slugs seeded from ROSTER_TIER_ALIASES (fast/balanced/deep).
// PB `model_tier` rows carry slug+display fields; a bot's `model_tier` relation
// points at the alias row for its tier.
const TIER_SLUG_TO_CLIENT = { fast: 'tier_fast', balanced: 'tier_balanced', deep: 'tier_deep' };

const MODULE_ICON = {
  introduction: 'Compass',
  headquarters: 'Grid',
  website: 'Globe',
  tasks: 'CheckSquare',
  finance: 'DollarSign',
  ads: 'Target',
  inbox: 'Inbox',
  files: 'Folder',
  bots: 'Cpu',
  settings: 'Settings',
  models: 'Cpu',
  marketplace: 'ShoppingBag',
  projects: 'Clipboard',
  calendar: 'Calendar'
};

const BOT_COLORS = ['#111827', '#5b8def', '#62c5a7', '#ef806e', '#9979dc', '#f3c969'];

async function getAll(collection, options = {}) {
  return pb.collection(collection).getFullList({ batch: 200, ...options });
}

/**
 * Hydrate the tenant workspace from PB. Assumes pb.authStore.isValid.
 * Returns a partial `state` patch (same keys as dataClient state) or null when
 * the user has no business membership yet (e.g. brand-new unprovisioned user).
 * Throws on PB errors so the caller can surface them.
 */
export async function hydrateWorkspaceFromPb() {
  if (!pb.authStore.isValid) return null;

  // The user's memberships → the business they belong to (owner/staff/agency).
  const memberships = await getAll('business_members', { filter: `user="${pb.authStore.record.id}"` });
  const membership = memberships.find((m) => m.status === 'active') || memberships[0];
  if (!membership) return null; // not a tenant member yet

  const businessId = membership.business;
  const business = await pb.collection('business').getOne(businessId).catch(() => null);
  if (!business) return null;

  const plan = business.subscription_plan
    ? await pb.collection('subscription_plan').getOne(business.subscription_plan).catch(() => null)
    : null;

  const brandProfile = await pb.collection('brand_profile')
    .getFirstListItem(`business="${businessId}"`)
    .catch(() => null);

  const modules = await getAll('module');
  modules.sort((a, b) => (a.position || 0) - (b.position || 0));

  const tierRows = await getAll('model_tier');
  const tierBySlug = new Map(tierRows.map((row) => [row.slug, row]));

  const teams = await getAll('bot_team', { filter: `business="${businessId}"` });
  const teamModuleByTeamId = new Map();
  for (const team of teams) {
    // bot_team.module is a relation to a module record OR a module slug string.
    const moduleRel = team.module;
    const moduleRow = typeof moduleRel === 'string' && modules.find((m) => m.id === moduleRel)
      ? modules.find((m) => m.id === moduleRel)
      : null;
    teamModuleByTeamId.set(team.id, moduleRow?.slug || moduleRow?.id || team.slug || 'headquarters');
  }

  const botRows = await getAll('bot', { filter: `business="${businessId}"` });
  const nameSeen = new Map(); // team.id → count, for stable display names if needed
  const bots = botRows.map((row, index) => {
    const teamSlug = row.bot_team ? teamModuleByTeamId.get(row.bot_team) || 'headquarters' : null;
    const tierAliasRow = row.model_tier
      ? (Array.isArray(row.model_tier) ? tierBySlug.get(row.model_tier[0]) || tierRows.find((t) => t.id === row.model_tier[0]) : tierBySlug.get(row.model_tier) || tierRows.find((t) => t.id === row.model_tier))
      : null;
    // consultable_bots is a relation list of bot ids.
    const consultableBots = Array.isArray(row.consultable_bots) ? row.consultable_bots : [];
    const teamCount = (nameSeen.get(teamSlug) || 0) + 1;
    nameSeen.set(teamSlug, teamCount);
    return {
      id: row.id,
      slug: row.slug || `bot-${row.id}`,
      name: row.name,
      role: row.role || 'Specialist',
      teamId: row.bot_team || null,
      module: teamSlug, // module slug the team lives in (or null for lone bots)
      color: BOT_COLORS[(teamCount - 1) % BOT_COLORS.length] || BOT_COLORS[index % BOT_COLORS.length],
      enabled: Boolean(row.enabled ?? true),
      isLead: Boolean(row.is_lead),
      consultableBots,
      modelSource: 'byo', // every live bot resolves through a tenant BYO credential (owner decision)
      byoModelId: null,
      modelTier: TIER_SLUG_TO_CLIENT[tierAliasRow?.slug] || 'tier_balanced',
      agentClass: row.agent_class || 'chat_simple',
      tools: Array.isArray(row.tools) ? row.tools : [],
      systemPrompt: row.system_prompt || '',
      knowledgeScope: row.knowledge_scope || '',
      capabilities: Array.isArray(row.capabilities) ? row.capabilities : [],
      business: businessId
    };
  });

  // Client modelTiers: build display rows from the three roster aliases so the
  // Models screen + tier selectors keep working with the same ids.
  const modelTiers = ['deep', 'balanced', 'fast'].map((slug) => {
    const row = tierBySlug.get(slug) || tierRows.find((t) => t.slug === slug);
    return {
      id: TIER_SLUG_TO_CLIENT[slug],
      slug,
      name: row?.name ? tierDisplayName(row) : slug[0].toUpperCase() + slug.slice(1),
      speed: row?.speed_label || 'Auto',
      cost: row?.cost_label || '$$',
      description: row?.description || `${slug} reasoning tier`,
      applicableSections: Array.isArray(row?.applicable_sections) ? row.applicable_sections : []
    };
  });

  const teamsMapped = teams.map((team) => ({
    id: team.id,
    slug: team.slug || team.id,
    name: team.name,
    module: teamModuleByTeamId.get(team.id) || 'headquarters',
    enabled: Boolean(team.enabled ?? true)
  }));

  return {
    session: {
      user: {
        id: pb.authStore.record.id,
        email: pb.authStore.record.email,
        name: pb.authStore.record.name || pb.authStore.record.email,
        role: membership.role || 'owner'
      },
      authenticated: true
    },
    business: {
      id: business.id,
      name: business.name,
      slug: business.slug || null,
      subscriptionPlanId: plan?.id || null,
      aoUsedThisPeriod: business.ao_used_this_period || 0,
      aoPeriodResetAt: business.ao_period_reset_at || null
    },
    brandProfile: brandProfile
      ? {
          id: brandProfile.id,
          voice: brandProfile.voice || '',
          avoid: brandProfile.avoid || '',
          principles: Array.isArray(brandProfile.principles) ? brandProfile.principles : []
        }
      : { voice: '', avoid: '', principles: [] },
    modules: modules.map((m) => ({
      id: m.id,
      slug: m.slug,
      name: m.name,
      icon: MODULE_ICON[m.slug] || 'Grid',
      position: m.position || 0
    })),
    modelTiers,
    teams: teamsMapped,
    bots,
    subscriptionPlans: plan
      ? [{
          id: plan.id,
          slug: plan.slug,
          displayName: plan.display_name,
          monthlyAoAllowance: plan.monthly_ao_allowance || 0,
          memoryRetention: plan.memory_retention || 'seven_day',
          price: plan.price || 0
        }]
      : []
  };
}

function tierDisplayName(row) {
  // e.g. 'GLM-5.2' alias rows → friendly label; alias rows are named after the
  // model they point at, which is a fine display for the Models screen.
  return row.name || row.slug;
}
